import React, { useState, useRef, useEffect } from "react";

////////////////////////////////////////////////
// KML Parsing & Generation Utilities + Tests  //
////////////////////////////////////////////////

// We'll return an array of { name, lat, lng, heading }.
function parseKML(kmlString) {
  const parser = new DOMParser();
  const xmlDoc = parser.parseFromString(kmlString, "text/xml");

  const placemarks = Array.from(xmlDoc.getElementsByTagName("Placemark"));
  const results = placemarks.map((pm) => {
    const nameEl = pm.getElementsByTagName("name")[0];
    const iconStyleEl = pm.getElementsByTagName("IconStyle")[0];
    const headingEl = iconStyleEl ? iconStyleEl.getElementsByTagName("heading")[0] : null;
    const pointEl = pm.getElementsByTagName("Point")[0];
    const coordsEl = pointEl ? pointEl.getElementsByTagName("coordinates")[0] : null;

    const name = nameEl ? nameEl.textContent : "Untitled";
    const heading = headingEl ? parseFloat(headingEl.textContent || "0") : 0;
    let lat = 0;
    let lng = 0;

    if (coordsEl) {
      const coordsText = coordsEl.textContent?.trim();
      // KML coords are Lng, Lat, Alt
      const parts = coordsText ? coordsText.split(",") : [];
      if (parts.length >= 2) {
        lng = parseFloat(parts[0]);
        lat = parseFloat(parts[1]);
      }
    }

    return { name, lat, lng, heading };
  });

  return results;
}

function generateKML(placemarks) {
  let kmlStr = `<?xml version="1.0" encoding="UTF-8"?>\n`;
  kmlStr += `<kml xmlns="http://www.opengis.net/kml/2.2" xmlns:gx="http://www.google.com/kml/ext/2.2" xmlns:globalmapper="https://www.bluemarblegeo.com/globalmapper">\n`;
  kmlStr += `<Document>\n`;
  kmlStr += `  <Style id="survey-point">\n`;
  kmlStr += `    <IconStyle><Icon><href>https://maps.google.com/mapfiles/kml/pal4/icon49.png</href></Icon></IconStyle>\n`;
  kmlStr += `  </Style>\n`;

  placemarks.forEach((pm, idx) => {
    kmlStr += `  <Placemark>\n`;
    kmlStr += `    <name>${idx + 1}</name>\n`;
    kmlStr += `    <description>${pm.name}</description>\n`;
    kmlStr += `    <styleUrl>#survey-point</styleUrl>\n`;
    kmlStr += `    <Point>\n`;
    kmlStr += `      <coordinates>${pm.lng},${pm.lat},0</coordinates>\n`;
    kmlStr += `      <altitudeMode>clampToGround</altitudeMode>\n`;
    kmlStr += `      <extrude>0</extrude>\n`;
    kmlStr += `    </Point>\n`;
    kmlStr += `  </Placemark>\n`;
  });

  kmlStr += `</Document>\n</kml>`;
  return kmlStr;
}

// Minimal rotation slider
function RotationSlider({ heading, onChange }) {
  const handleChange = (e) => {
    onChange(parseFloat(e.target.value));
  };

  return (
    <input
      type="range"
      min={0}
      max={359}
      value={heading}
      onChange={handleChange}
      className="w-32"
    />
  );
}

////////////////////////////////////////////////////////////
// A simple OpenLayers-based approach without API keys.    //
// All placemarks move/rotate/scale as a group.           //
////////////////////////////////////////////////////////////

import "ol/ol.css";
import { Map, View } from "ol";
import TileLayer from "ol/layer/Tile";
import { OSM } from "ol/source";
import XYZ from "ol/source/XYZ";
import { fromLonLat, toLonLat } from "ol/proj";
import { Feature } from "ol";
import { Point } from "ol/geom";
import { Style, Fill, Stroke, Text } from "ol/style";

function latLonToXY(lat, lon) {
  return fromLonLat([lon, lat]); // returns [x,y] in WebMercator.
}

function xyToLatLon(x, y) {
  const [lon, lat] = toLonLat([x, y]);
  return { lat, lon };
}

function rotateXY(x, y, angleDeg) {
  const theta = (angleDeg * Math.PI) / 180;
  const rx = x * Math.cos(theta) - y * Math.sin(theta);
  const ry = x * Math.sin(theta) + y * Math.cos(theta);
  return [rx, ry];
}

export default function KMLMarkerEditor() {
  const [placemarks, setPlacemarks] = useState([]);

  // group center that user can modify
  const [groupCenter, setGroupCenter] = useState({ lat: 0, lon: 0 });
  const [groupHeading, setGroupHeading] = useState(0);
  // new: group scale factor
  const [groupScale, setGroupScale] = useState(1);

  // original data from file
  const [originalData, setOriginalData] = useState([]);

  // we store the original group center (in XY) and each placemark's XY offset from that center.
  const [originalGroupCenterXY, setOriginalGroupCenterXY] = useState([0, 0]);
  const [relativeOffsets, setRelativeOffsets] = useState([]);

  // map reference
  const mapRef = useRef(null);

  // layers for normal vs satellite
  const osmLayerRef = useRef(null);
  const satLayerRef = useRef(null);

  // toggles
  const [isSatellite, setIsSatellite] = useState(false);
  const [zoom, setZoom] = useState(17);

  // features for placemark drawing
  const [features, setFeatures] = useState([]);

  useEffect(() => {
    // create two tile layers
    const osmLayer = new TileLayer({
      source: new OSM(),
      visible: true,
    });

    // Use ArcGIS world imagery for satellite
    const satellite = new TileLayer({
      source: new XYZ({
        url: "https://server.arcgisonline.com/ArcGIS/rest/services/World_Imagery/MapServer/tile/{z}/{y}/{x}",
      }),
      visible: false,
    });

    osmLayerRef.current = osmLayer;
    satLayerRef.current = satellite;

    const map = new Map({
      target: "olmap",
      layers: [osmLayer, satellite],
      view: new View({
        center: fromLonLat([0, 0]),
        zoom,
      }),
    });

    mapRef.current = map;

    // track map zoom changes
    map.on("moveend", () => {
      const newZoom = map.getView().getZoom();
      setZoom(Math.round(newZoom));
    });

    // singleclick => set group center
    map.on("singleclick", (evt) => {
      const coord = evt.coordinate; // [x,y]
      const { lat, lon } = xyToLatLon(coord[0], coord[1]);
      setGroupCenter({ lat, lon });
    });

    return () => {
      map.setTarget(null);
    };
  }, []);

  // watch isSatellite => toggle layers
  useEffect(() => {
    if (!mapRef.current || !osmLayerRef.current || !satLayerRef.current) return;
    osmLayerRef.current.setVisible(!isSatellite);
    satLayerRef.current.setVisible(isSatellite);
  }, [isSatellite]);

  // watch zoom => set map zoom if changed by slider
  useEffect(() => {
    if (!mapRef.current) return;
    const currentZoom = mapRef.current.getView().getZoom();
    if (zoom !== Math.round(currentZoom)) {
      mapRef.current.getView().setZoom(zoom);
    }
  }, [zoom]);

  // test parse/generate KML
  useEffect(() => {
    function runTests() {
      const sampleKML = `\n        <kml xmlns="http://www.opengis.net/kml/2.2">\n          <Document>\n            <Placemark>\n              <name>Marker 1</name>\n              <Style><IconStyle><heading>45</heading></IconStyle></Style>\n              <Point>\n                <coordinates>-123.456,12.3456,0</coordinates>\n              </Point>\n            </Placemark>\n          </Document>\n        </kml>\n      `;
      const result = parseKML(sampleKML);
      console.assert(result.length === 1, "Expected 1 placemark");
      console.assert(result[0].name === "Marker 1", "Expected name=Marker 1");
      console.assert(result[0].heading === 45, "Expected heading=45");
      console.assert(result[0].lat === 12.3456, "Expected lat=12.3456");
      console.assert(result[0].lng === -123.456, "Expected lng=-123.456");

      const sampleKML2 = `\n        <kml xmlns=\"http://www.opengis.net/kml/2.2\">\n          <Document>\n            <Placemark>\n              <name>Marker A</name>\n              <Style><IconStyle><heading>0</heading></IconStyle></Style>\n              <Point>\n                <coordinates>10.0,50.0,0</coordinates>\n              </Point>\n            </Placemark>\n            <Placemark>\n              <name>Marker B</name>\n              <Style><IconStyle><heading>90</heading></IconStyle></Style>\n              <Point>\n                <coordinates>20.0,60.0,0</coordinates>\n              </Point>\n            </Placemark>\n          </Document>\n        </kml>\n      `;
      const result2 = parseKML(sampleKML2);
      console.assert(result2.length === 2, "Expected 2 placemarks");
      console.assert(result2[0].name === "Marker A", "Expected name=Marker A");
      console.assert(result2[1].heading === 90, "Expected heading=90 for Marker B");

      const testPlacemarks = [
        { name: "Test1", lat: 10, lng: 20, heading: 30 },
        { name: "Test2", lat: -5.5, lng: 100, heading: 270 },
      ];
      const kmlOut = generateKML(testPlacemarks);
      console.assert(kmlOut.includes("Test1"), "Expected Test1 in output");
      console.assert(kmlOut.includes("Test2"), "Expected Test2 in output");
      console.log("parseKML / generateKML tests passed!");
    }
    runTests();
  }, []);

  const fileInputRef = useRef(null);

  const handleFileChange = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    const text = await file.text();
    const parsed = parseKML(text);

    if (parsed.length > 0 && mapRef.current) {
      mapRef.current.getView().setCenter(fromLonLat([parsed[0].lng, parsed[0].lat]));
      mapRef.current.getView().setZoom(16);
    }

    setOriginalData(parsed);

    // compute average center for the data
    let latSum = 0;
    let lngSum = 0;
    parsed.forEach((p) => {
      latSum += p.lat;
      lngSum += p.lng;
    });
    let latAvg = 0;
    let lngAvg = 0;
    if (parsed.length > 0) {
      latAvg = latSum / parsed.length;
      lngAvg = lngSum / parsed.length;
    }
    setGroupCenter({ lat: latAvg, lon: lngAvg });
    setGroupHeading(0);
    setGroupScale(1);

    const groupCenterXY = latLonToXY(latAvg, lngAvg);
    setOriginalGroupCenterXY(groupCenterXY);

    // for each placemark, compute offset in XY from that center
    const offsets = parsed.map((p) => {
      const pXY = latLonToXY(p.lat, p.lng);
      return [pXY[0] - groupCenterXY[0], pXY[1] - groupCenterXY[1]];
    });
    setRelativeOffsets(offsets);
  };

  // recalc placemarks on group transform
  useEffect(() => {
    if (!mapRef.current || originalData.length === 0) return;

    const newCenterXY = latLonToXY(groupCenter.lat, groupCenter.lon);
    const updatedPlacemarks = originalData.map((orig, i) => {
      const offset = relativeOffsets[i];
      // rotate
      const [rx, ry] = rotateXY(offset[0], offset[1], groupHeading);
      // scale
      const sx = rx * groupScale;
      const sy = ry * groupScale;
      // translate
      const px = newCenterXY[0] + sx;
      const py = newCenterXY[1] + sy;
      const { lat, lon } = xyToLatLon(px, py);

      return {
        name: orig.name,
        heading: orig.heading,
        lat,
        lng: lon,
      };
    });

    setPlacemarks(updatedPlacemarks);
  }, [groupCenter, groupHeading, groupScale, originalData, relativeOffsets]);

  // build features whenever placemarks changes
  useEffect(() => {
    if (!mapRef.current || placemarks.length === 0) {
      setFeatures([]);
      return;
    }

    const feats = placemarks.map((pm) => {
      const f = new Feature({
        geometry: new Point(fromLonLat([pm.lng, pm.lat])),
      });
      // small 'x'
      f.setStyle(
        new Style({
          text: new Text({
            text: "x",
            font: "bold 14px sans-serif",
            fill: new Fill({ color: "#000" }),
            stroke: new Stroke({ color: "#fff", width: 2 }),
          }),
        })
      );
      return f;
    });
    setFeatures(feats);
  }, [placemarks]);

  // attach features to vector layer
  useEffect(() => {
    if (!mapRef.current) return;

    // remove old layer if present
    const existingLayers = mapRef.current.getLayers().getArray();
    // we know 0=OSM,1=Sat => remove index 2 if present
    if (existingLayers[2]) {
      mapRef.current.removeLayer(existingLayers[2]);
    }

    if (!features.length) {
      return;
    }

    import("ol/layer/Vector").then((module) => {
      const VectorLayer = module.default;
      import("ol/source/Vector").then((module2) => {
        const VectorSource = module2.default;
        const vectorSrc = new VectorSource({ features });
        const vectorLyr = new VectorLayer({ source: vectorSrc });
        mapRef.current.addLayer(vectorLyr);
      });
    });
  }, [features]);

  function handleGroupCenterChange(latOrLon, val) {
    if (latOrLon === "lat") {
      setGroupCenter((c) => ({ ...c, lat: parseFloat(val) }));
    } else {
      setGroupCenter((c) => ({ ...c, lon: parseFloat(val) }));
    }
  }

  function handleGroupHeadingChange(val) {
    setGroupHeading(parseFloat(val));
  }

  function handleGroupScaleChange(val) {
    setGroupScale(parseFloat(val));
  }

  const handleExportKML = () => {
    const kmlStr = generateKML(placemarks);
    console.log("KML body:", kmlStr);
    // Some browsers or envs block programmatic downloads.
    // The fallback is console printing.
    try {
      const blob = new Blob([kmlStr], { type: "application/vnd.google-earth.kml+xml" });
      const url = URL.createObjectURL(blob);
      const link = document.createElement("a");
      link.href = url;
      link.download = "edited.kml";
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      URL.revokeObjectURL(url);
    } catch (err) {
      console.error("Export KML error:", err);
    }
  };

  return (
    <div className="p-4 space-y-4">
      <p className="text-sm text-gray-700">
        Perform bulk transformations on KML placemarkers. <br/>
        Rotate, scale using the sliders. Click on the map or enter lat/lon to set the center point. <br/>
        Export to save the transformed KML.<br/>
      </p>

      <div className="flex items-center space-x-2 mb-2">
        <input
          type="file"
          ref={fileInputRef}
          accept=".kml"
          onChange={handleFileChange}
          className="file:mr-2 file:py-1 file:px-2 file:border file:rounded file:bg-gray-200"
        />
        <button
          onClick={handleExportKML}
          className="bg-blue-500 text-white px-4 py-2 rounded-lg hover:bg-blue-600 shadow"
        >
          Export KML
        </button>
      </div>

      <div className="flex items-center space-x-4 mb-2">
        <label className="flex items-center space-x-1">
          <input
            type="checkbox"
            checked={isSatellite}
            onChange={(e) => setIsSatellite(e.target.checked)}
          />
          <span className="text-sm text-gray-700">Satellite view</span>
        </label>

        {/* <div className="flex items-center space-x-2">
          <label className="text-sm text-gray-600">Map Zoom:</label>
          <input
            type="range"
            min={1}
            max={19}
            value={zoom}
            onChange={(e) => setZoom(parseInt(e.target.value, 10))}
            className="w-32"
          />
          <span className="text-sm w-8 inline-block text-right">{zoom}</span>
        </div> */}
      </div>

      <div className="flex space-x-4 mb-2">
        <div className="space-x-2">
          <label className="text-sm text-gray-600">Center Lat:</label>
          <input
            type="number"
            step="0.0001"
            value={groupCenter.lat}
            onChange={(e) => handleGroupCenterChange("lat", e.target.value)}
            className="border rounded px-1 w-28"
          />
        </div>
        <div className="space-x-2">
          <label className="text-sm text-gray-600">Center Lon:</label>
          <input
            type="number"
            step="0.0001"
            value={groupCenter.lon}
            onChange={(e) => handleGroupCenterChange("lon", e.target.value)}
            className="border rounded px-1 w-28"
          />
        </div>
        <div className="space-x-2 flex items-center">
          <label className="text-sm text-gray-600">Rotate:</label>
          <RotationSlider heading={groupHeading} onChange={(val) => handleGroupHeadingChange(val)} />
          <span className="text-sm w-8 inline-block text-right">{groupHeading}°</span>
        </div>
        <div className="space-x-2 flex items-center">
          <label className="text-sm text-gray-600">Scale:</label>
          <input
            type="range"
            min={0.1}
            max={5}
            step={0.1}
            value={groupScale}
            onChange={(e) => handleGroupScaleChange(e.target.value)}
            className="w-32"
          />
          <span className="text-sm w-8 inline-block text-right">{groupScale.toFixed(1)}x</span>
        </div>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-5 gap-4">
        <div className="col-span-4 w-full h-[800px] rounded-2xl overflow-hidden shadow-lg">
          <div id="olmap" style={{ width: "100%", height: "100%" }}></div>
        </div>

        <div className="col-span-1 space-y-2 overflow-y-auto max-h-[800px]">
          <h2 className="text-xl font-semibold">Placemarks</h2>
          {placemarks.map((pm, i) => (
            <div key={i} className="p-3 rounded-2xl shadow bg-white space-y-2 flex flex-col">
              <h3 className="text-lg font-medium">{pm.name || `Placemark ${i + 1}`}</h3>
              <div className="text-sm text-gray-700">
               {pm.lat.toFixed(5)}, {pm.lng.toFixed(5)}
              </div>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
}
